import { z } from "zod";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import Cookies from "js-cookie";
import { MdClose } from "react-icons/md";

import {
  AlertDialog,
  AlertDialogContent,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { useAnnouncementModal } from "./use-announcement-modal";
import { getAllEmployeesQueryOptions } from "@/config/queries/query-options";
import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";
import { Textarea } from "@/components/ui/textarea/textarea";
import MultipleSelector from "@/components/ui/multiple-selector/multiple-selector";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { UploadAttachment } from "./upload-attachement";
import { createNewAnnouncement } from "@/config/mutation/admin";
import { toast } from "sonner";
import { AxiosError } from "axios";

const formSchema = z.object({
  title: z.string().min(2).max(50),
  description: z.string(),
  files: z.array(z.string()).optional(),
  recipients: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    })
  ),
  toAll: z.boolean().default(false),
});

export type FormValues = z.infer<typeof formSchema>;

export const Announcement = () => {
  const { open: openAnnouncement, onOpenChange: onOpenAnnouncement } =
    useAnnouncementModal();
  const queryClient = useQueryClient();

  const user = Cookies.get("key") as string;

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      toAll: false,
      description: "",
      recipients: [],
      files: [],
    },
  });

  const recipients = useWatch({
    name: "recipients",
    control: form.control,
  });

  const toAll = useWatch({
    name: "toAll",
    control: form.control,
  });

  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn: createNewAnnouncement(user),
  });

  const { data, isPending, isFetching } = useQuery(
    getAllEmployeesQueryOptions(user)
  );

  const tempEmployeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.employees === undefined) {
      return [];
    }

    const result = data?.employees?.map((emp) => {
      return {
        value: emp?._id,
        label: emp?.name,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  const employeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.employees === undefined) {
      return [];
    }

    const result = data?.employees?.map((emp) => {
      return {
        value: emp?.name,
        label: emp?.name,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    const recipients = values?.recipients?.map((rec) => {
      const findId = tempEmployeesData?.find((t) => t?.label === rec.value);

      return findId?.value;
    });

    const realData = {
      ...values,
      recipients: values?.toAll
        ? tempEmployeesData?.map((emp) => emp?.value)
        : recipients,
    };

    mutate(realData, {
      onSuccess: async () => {
        // console.log("New Employee", data);
        await queryClient.invalidateQueries({
          queryKey: ["admin-announcements"],
        });

        toast.success("New Announcement Created!");
        onOpenAnnouncement(false);

        form.reset();
      },

      onError: (error) => {
        console.log("ERROR WHILE CREATING ANNOUNCEMENT", error);
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });
  }

  return (
    <>
      <AlertDialog open={openAnnouncement} onOpenChange={onOpenAnnouncement}>
        <AlertDialogContent className="p-0 max-w-[800px]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="border-b px-3 py-2 flex items-center justify-between">
                <h3 className="text-xl font-medium tracking-normal">
                  Create New Announcement
                </h3>
                <div className="relative flex items-center gap-4">
                  <Button size="sm" disabled={isSubmitting}>
                    Create
                  </Button>
                  <button
                    disabled={isSubmitting}
                    type="button"
                    className="p-1 rounded-md transition-all duration-300 hover:bg-accent"
                    onClick={() => {
                      onOpenAnnouncement(false);
                      form.reset();
                    }}
                  >
                    <MdClose className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-3 p-5 max-h-[700px] overflow-y-auto">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input type="text" disabled={isSubmitting} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea rows={3} disabled={isSubmitting} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="toAll"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                      <FormControl>
                        <Checkbox
                          disabled={isSubmitting}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Want to send to all employees?</FormLabel>
                        <FormDescription>
                          Check this if you want all employees to have this
                          announcement. otherwise choose employees below.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                <div className="flex flex-col">
                  <h4 className="text-sm mb-2 font-medium">Recipients</h4>
                  <MultipleSelector
                    disabled={toAll || isSubmitting}
                    options={employeesData}
                    value={recipients}
                    onChange={(options) => {
                      form.setValue("recipients", options);
                    }}
                    className="disabled:pointer-events-none disabled:opacity-40"
                    emptyIndicator={
                      <p className="text-center text-lg text-gray-600 dark:text-gray-400">
                        no more employees found.
                      </p>
                    }
                  />
                </div>

                <div>
                  <FormField
                    control={form.control}
                    name="files"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Upload Attachment</FormLabel>
                        <FormControl>
                          <UploadAttachment
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
